import { lazy, Suspense, useMemo } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core/theme';
import { InternationalizationProvider } from '@astryxdesign/core/i18n';
import ruRU from '@astryxdesign/core/locales/ru-RU.generated.js';
import { Center } from '@astryxdesign/core/Center';
import { Spinner } from '@astryxdesign/core/Spinner';
import { tenantBase } from '@/lib/env';
import { TenantProvider } from './tenant-context';
import { ClientShell } from '@/client/ClientShell';
import { HomePage } from '@/client/HomePage';
import { ServicesPage } from '@/client/ServicesPage';
import { MyBookingPage } from '@/client/MyBookingPage';
import { NotFound } from './NotFound';
import { RouteError } from './RouteError';
import { bootTheme } from '@/theme/tenant-theme';

// The owner cabinet is a separate chunk: clients never download it.
const OwnerApp = lazy(() => import('@/owner/OwnerApp'));

function Loading() {
  return (
    <Center minHeight="60dvh">
      <Spinner size="lg" label="Загрузка" />
    </Center>
  );
}

export function App({ slug }: { slug: string }) {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, retry: (n, e) => n < 2 && (e as { status?: number }).status !== 404 },
          mutations: { retry: false },
        },
      }),
    [],
  );

  const router = useMemo(
    () =>
      createBrowserRouter(
        [
          {
            element: (
              <TenantProvider slug={slug}>
                <Outlet />
              </TenantProvider>
            ),
            errorElement: <RouteError />,
            children: [
              {
                element: <ClientShell />,
                children: [
                  { index: true, element: <HomePage /> },
                  { path: 'services', element: <ServicesPage /> },
                  { path: 'book', element: <HomePage /> },
                  { path: 'my', element: <MyBookingPage /> },
                  { path: 'my/:bookingId', element: <MyBookingPage /> },
                ],
              },
              {
                path: 'owner/*',
                element: (
                  <Suspense fallback={<Loading />}>
                    <OwnerApp />
                  </Suspense>
                ),
              },
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
        { basename: tenantBase(slug) },
      ),
    [slug],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <InternationalizationProvider locale="ru-RU" messages={{ 'ru-RU': ruRU }}>
        <Theme theme={bootTheme()} mode="dark">
          <RouterProvider router={router} />
        </Theme>
      </InternationalizationProvider>
    </QueryClientProvider>
  );
}
